/**
 * triggerMetrics — one honest reading of a trigger row's fire/success numbers.
 *
 * Two defects this exists to close:
 *
 *  1. Every trigger table rendered `times_triggered > 0 ? rate : 100`, so a
 *     trigger that has NEVER fired displayed a green "100.0%" success rate.
 *     There is no such rate — a never-fired trigger has no success rate, and
 *     that renders '—' (TriggerHealthPage already did this correctly).
 *
 *  2. `times_triggered` / `times_sent_successfully` on email_triggers are
 *     LIFETIME counters, but the columns were captioned "Fires (24h)" and the
 *     pages claimed "last 30 days". The email-analytics endpoints are gaining
 *     period-scoped fields per the M5 wire contract:
 *
 *        fires_period:        integer
 *        success_rate_period: number | null
 *
 *     When those are present we use them and the surface may legitimately
 *     say "in this period". When they are absent (older backend still
 *     deployed) we fall back to the lifetime counters AND the caller must
 *     label them as all-time — `periodScoped` says which case you are in.
 */

export interface TriggerFireFields {
  times_triggered?: number | string | null;
  times_sent_successfully?: number | string | null;
  /** Some endpoints precompute the lifetime rate. */
  success_rate?: number | string | null;
  /** M5 wire contract — period-scoped fire count. */
  fires_period?: number | string | null;
  /** M5 wire contract — period-scoped success rate (null when nothing fired). */
  success_rate_period?: number | string | null;
}

export interface TriggerFireView {
  /** Fires in the reported window; null when unknown. */
  fires: number | null;
  /** Success rate as a percentage; null when nothing fired / unknown. */
  successRate: number | null;
  /** True when `fires`/`successRate` came from the period-scoped fields. */
  periodScoped: boolean;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function triggerFireView(row: TriggerFireFields | null | undefined): TriggerFireView {
  const periodFires = num(row?.fires_period);
  if (periodFires !== null) {
    const rate = num(row?.success_rate_period);
    return {
      fires: periodFires,
      // A never-fired trigger has no rate, whatever the server sent.
      successRate: periodFires > 0 ? rate : null,
      periodScoped: true,
    };
  }
  const fires = num(row?.times_triggered);
  if (fires === null || fires <= 0) {
    return { fires: fires ?? null, successRate: null, periodScoped: false };
  }
  const precomputed = num(row?.success_rate);
  if (precomputed !== null) {
    return { fires, successRate: precomputed, periodScoped: false };
  }
  const ok = num(row?.times_sent_successfully) ?? 0;
  return { fires, successRate: (ok / fires) * 100, periodScoped: false };
}

/** True when at least one row carries the period-scoped fields. */
export function anyPeriodScoped(rows: TriggerFireFields[] | null | undefined): boolean {
  return (rows ?? []).some((r) => num(r?.fires_period) !== null);
}

/**
 * Aggregate fires + successes across rows. Returns nulls rather than zeros when
 * nothing is known, so an aggregate SLA tile can render '—' instead of a
 * fabricated 100%.
 */
export function aggregateTriggerFires(rows: TriggerFireFields[] | null | undefined): {
  fires: number | null;
  successRate: number | null;
  periodScoped: boolean;
} {
  const list = rows ?? [];
  if (list.length === 0) return { fires: null, successRate: null, periodScoped: false };
  const periodScoped = anyPeriodScoped(list);
  let fires = 0;
  let weighted = 0;
  let known = false;
  for (const r of list) {
    const v = triggerFireView(r);
    if (v.fires == null) continue;
    known = true;
    fires += v.fires;
    if (v.successRate != null) weighted += (v.successRate / 100) * v.fires;
  }
  if (!known) return { fires: null, successRate: null, periodScoped };
  return {
    fires,
    successRate: fires > 0 ? (weighted / fires) * 100 : null,
    periodScoped,
  };
}

/** Percentage formatter that never invents a number. */
export const fmtRate = (rate: number | null, digits = 1): string =>
  rate == null ? "—" : `${rate.toFixed(digits)}%`;

/** Count formatter that never invents a number. */
export const fmtFires = (n: number | null): string =>
  n == null ? "—" : n.toLocaleString();

/**
 * Colour for a success rate. `null` (no fires) stays neutral — a trigger that
 * has never fired is neither healthy nor broken.
 */
export const rateColor = (rate: number | null): string => {
  if (rate == null) return "text.secondary";
  if (rate >= 95) return "#146152";
  if (rate >= 80) return "#D4AC0D";
  return "#7D1935";
};
